/**
 * Search Product Storage Service
 * Handles storing search results in MongoDB with S3 image uploads
 * Implements priority search: Solr → MongoDB → Alibaba API
 */

const API_URL = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';

// Product display configuration from environment variables
const DEFAULT_PAGE_SIZE = parseInt(process.env.REACT_APP_TOTAL_PRODUCTS || '12', 10);

class SearchProductStorageService {
  /**
   * Store search results in MongoDB
   * @param {Array} products - Array of products from search results
   * @param {string} keyword - Search keyword
   * @param {number} pageNum - Page number
   * @param {number} totalPages - Total pages
   * @param {boolean} uploadToS3 - Whether to upload images to S3 (default: true)
   * @returns {Promise<Object>} - Result with counts
   */
  static async storeSearchResults(products, keyword, pageNum = 1, totalPages = 1, uploadToS3 = true, imageId = null) {
    try {
      const body = { products, keyword, pageNum, totalPages, uploadToS3 };
      if (imageId != null && imageId !== '') body.imageId = String(imageId);
      const response = await fetch(`${API_URL}admin/search-products/store`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to store search results: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[SearchProductStorageService] Store error:', error);
      throw error;
    }
  }

  /**
   * Get products by search keyword
   * @param {string} keyword - Search keyword
   * @param {Object} options - Query options
   * @returns {Promise<Object>} - Products and pagination info
   */
  static async getProductsByKeyword(keyword, options = {}) {
    try {
      const { page = 1, limit = 50, status } = options;
      const params = new URLSearchParams({
        keyword,
        page: String(page),
        limit: String(limit),
      });

      if (status) {
        params.append('status', status);
      }

      const response = await fetch(`${API_URL}admin/search-products/by-keyword?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to get products: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[SearchProductStorageService] Get products error:', error);
      throw error;
    }
  }

  /**
   * Priority Search: Solr → MongoDB → Alibaba API
   * Returns results from the first successful source for fast performance
   * @param {string} keyword - Search keyword
   * @param {number} page - Page number (1-indexed)
   * @param {number} pageSize - Results per page
   * @param {boolean} forceAlibaba - Force Alibaba API call (skip cache)
   * @returns {Promise<Object>} - Search results with source info
   */
  static async prioritySearch(keyword, page = 1, pageSize = 50, forceAlibaba = false) {
    try {
      const params = new URLSearchParams({
        keyword,
        page: String(page),
        pageSize: String(pageSize),
        forceAlibaba: String(forceAlibaba),
      });

      const response = await fetch(`${API_URL}admin/search-products/priority-search?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Priority search failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status?.code === 200 && result.data) {
        return {
          success: true,
          source: result.data.source, // 'solr', 'mongodb', or 'alibaba'
          products: result.data.products || [],
          total: result.data.total || 0,
          page: result.data.page || 1,
          totalPages: result.data.totalPages || 1,
          searchTime: result.data.searchTime || 0,
          rawResponse: result.data.rawResponse, // For Alibaba API response
        };
      }

      return { success: false, products: [], total: 0, source: 'none' };
    } catch (error) {
      console.error('[SearchProductStorageService] Priority search error:', error);
      return { success: false, error: error.message, products: [], total: 0, source: 'error' };
    }
  }

  /**
   * Parallel Priority Search (faster)
   * Starts Solr and MongoDB in parallel, falls back to Alibaba if needed
   * @param {string} keyword - Search keyword
   * @param {number} page - Page number (1-indexed)
   * @param {number} pageSize - Results per page
   * @returns {Promise<Object>} - Search results from fastest successful source
   */
  static async parallelSearch(keyword, page = 1, pageSize = 50) {
    try {
      const params = new URLSearchParams({
        keyword,
        page: String(page),
        pageSize: String(pageSize),
      });

      const response = await fetch(`${API_URL}admin/search-products/parallel-search?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Parallel search failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status?.code === 200 && result.data) {
        return {
          success: true,
          source: result.data.source,
          products: result.data.products || [],
          total: result.data.total || 0,
          page: result.data.page || 1,
          totalPages: result.data.totalPages || 1,
          searchTime: result.data.searchTime || 0,
          rawResponse: result.data.rawResponse,
        };
      }

      return { success: false, products: [], total: 0, source: 'none' };
    } catch (error) {
      console.error('[SearchProductStorageService] Parallel search error:', error);
      return { success: false, error: error.message, products: [], total: 0, source: 'error' };
    }
  }

  /**
   * Combined Priority Search: Returns products based on pageSize parameter
   * Combines results from Solr → MongoDB → Alibaba API to fill up to pageSize products
   * @param {string} keyword - Search keyword
   * @param {number} page - Page number (1-indexed)
   * @param {number} pageSize - Results per page (default: from REACT_APP_TOTAL_PRODUCTS env var)
   * @param {boolean} forceAlibaba - Force Alibaba API call (skip cache)
   * @returns {Promise<Object>} - Combined search results from multiple sources
   */
  static async combinedSearch(keyword, page = 1, pageSize = DEFAULT_PAGE_SIZE, forceAlibaba = false, categoryId = null) {
    try {
      const params = new URLSearchParams({
        keyword,
        page: String(page),
        pageSize: String(pageSize),
        forceAlibaba: String(forceAlibaba),
      });

      if (categoryId !== null && categoryId !== undefined && String(categoryId).trim() !== '') {
        params.set('categoryId', String(categoryId).trim());
      }

      const response = await fetch(`${API_URL}admin/search-products/combined-search?${params.toString()}`);

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const errorMessage = errBody?.error?.message || errBody?.message || `Combined search failed: ${response.status}`;
        return {
          success: false,
          statusCode: response.status,
          error: errorMessage,
          products: [],
          total: 0,
          source: 'none',
          sources: [],
        };
      }

      const result = await response.json();
      
      if (result.status?.code === 200 && result.data) {
        return {
          success: true,
          source: result.data.source, // Primary source for backward compatibility
          sources: result.data.sources || [result.data.source], // All sources that contributed
          products: result.data.products || [],
          total: result.data.total || 0,
          mongoTotal: result.data.mongoTotal || 0, // DB product count for pagination offset
          page: result.data.page || 1,
          totalPages: result.data.totalPages || 1,
          searchTime: result.data.searchTime || 0,
          rawResponse: result.data.rawResponse, // For Alibaba API response
          isCombined: result.data.isCombined || false, // Flag indicating multiple sources were used
        };
      }

      return {
        success: false,
        statusCode: result.status?.code,
        error: result.error?.message || result.error,
        products: [],
        total: 0,
        source: 'none',
        sources: [],
      };
    } catch (error) {
      console.error('[SearchProductStorageService] Combined search error:', error);
      const statusFromMessage = error.message?.includes('400') ? 400 : undefined;
      return {
        success: false,
        statusCode: statusFromMessage,
        error: error.message,
        products: [],
        total: 0,
        source: 'error',
        sources: [],
      };
    }
  }

  /**
   * Store search results in background (non-blocking)
   * Uses fetch with keepalive to ensure request completes even if user navigates away
   * @param {Array} products - Array of products from search results
   * @param {string} keyword - Search keyword
   * @param {number} pageNum - Page number
   * @param {number} totalPages - Total pages
   */
  static storeInBackground(products, keyword, pageNum = 1, totalPages = 1, imageId = null) {
    console.log(`[SearchProductStorageService] 📦 Starting background storage:`, {
      keyword,
      productCount: Array.isArray(products) ? products.length : 0,
      pageNum,
      totalPages,
    });

    if (!Array.isArray(products) || products.length === 0) {
      console.warn(`[SearchProductStorageService] ⚠️ No products to store!`);
      return;
    }

    if (!keyword || typeof keyword !== 'string' || keyword.trim() === '') {
      console.warn(`[SearchProductStorageService] ⚠️ Invalid keyword: "${keyword}"`);
      return;
    }

    // Use fetch with keepalive to ensure request completes even on page navigation
    // keepalive allows the request to outlive the page
    const payload = JSON.stringify({
      products,
      keyword,
      pageNum,
      totalPages,
      uploadToS3: true,
      ...(imageId != null && imageId !== '' ? { imageId: String(imageId) } : {}),
    });

    // Check payload size - keepalive has a 64KB limit
    const payloadSize = new Blob([payload]).size;
    const useKeepalive = payloadSize < 64000; // 64KB limit for keepalive
    
    if (payloadSize > 64000) {
      console.warn(`[SearchProductStorageService] ⚠️ Payload size (${payloadSize} bytes) exceeds keepalive limit, request may fail if page navigates`);
    }

    console.log(`[SearchProductStorageService] 📤 Sending request to: ${API_URL}admin/search-products/store`, {
      payloadSize,
      useKeepalive,
      sampleProduct: products[0] ? {
        offerId: products[0].offerId || products[0].id,
        title: products[0].subjectTrans || products[0].subject || products[0].title,
        hasDetailedInfo: !!(products[0].detailed_info && Object.keys(products[0].detailed_info || {}).length > 0),
      } : null,
    });

    fetch(`${API_URL}admin/search-products/store`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: payload,
      keepalive: useKeepalive, // Ensures request completes even if page unloads
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(`Store failed: ${response.status}`);
      })
      .then(result => {
        if (result?.status?.code === 200) {
          const data = result.data || result;
          console.log(`[SearchProductStorageService] Background store complete:`, {
            stored: data.stored || 0,
            updated: data.updated || 0,
            skipped: data.skipped || 0,
            message: data.message,
            errors: data.errors,
          });

          // ⚡ Notify product cards that these products are now in DB (triggers price color change)
          const ids = data.storedOfferIds || [];
          if (ids.length > 0) {
            window.dispatchEvent(new CustomEvent('productsStoredInDB', { detail: { offerIds: ids } }));
          }
          
          // Log warnings if there were errors or no products were stored
          if (data.errors && data.errors.length > 0) {
            console.warn(`[SearchProductStorageService] Some products failed to store:`, data.errors);
          }
          if ((data.stored || 0) === 0 && (data.updated || 0) === 0) {
            console.warn(`[SearchProductStorageService] No products were stored or updated. Skipped: ${data.skipped || 0}`);
          }
        } else {
          console.warn(`[SearchProductStorageService] Store response error:`, result);
        }
      })
      .catch(error => {
        console.error('[SearchProductStorageService] Background store failed:', {
          message: error.message,
          stack: error.stack,
          keyword: keyword,
          productCount: products?.length || 0,
        });
      });
  }
}

export default SearchProductStorageService;
